// «Реклама» → «Разрезы»: кто и где видит рекламу (разрезы Meta — возраст, пол, регион, место показа, устройство)
// и как сеть решает наших лидов по разрезам (час и день недели, место показа, ввод номера, риск антифрода,
// браузер, причины отказа). Фильтры — общие со страницей. Все цифры считает api (/api/ads/breakdowns),
// страница только показывает: полоски — доля в расходе и % апрува, «мало данных» — пока цифры случайны.
import { type Period, periodPhrase } from '@autoaff/schemas/period';
import { Button, EmptyState, int, pct, Segmented, Skeleton, usd } from '@autoaff/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ChartNoAxesColumn, Users } from 'lucide-react';
import { api } from '../../api';
import { FilterSelect, useRemembered } from '../../filters';
import { type AdsMode, MODES } from './AdsTable';
import './breakdowns.css';

// ─── данные ────────────────────────────────────────────────────────────
export interface MetaCut {
  key: string;
  spend: number;
  impressions: number;
  clicks: number;
  linkClicks: number;
  /** null — Meta не отдаёт лиды пикселя в этом разрезе (регион). */
  leadsFb: number | null;
  video3s: number;
  ctr: number | null;
  cpm: number | null;
  cpl: number | null;
  hookRate: number | null;
  share: number | null;
  small: boolean;
}

export interface LeadCut {
  key: string;
  leads: number;
  approved: number;
  rejected: number;
  trash: number;
  double: number;
  inProcess: number;
  decided: number;
  approveRate: number | null;
  junkRate: number | null;
  small: boolean;
}

export interface BreakdownsData {
  period: { key: string; since: string | null; until: string };
  meta: {
    from: string | null;
    syncedAt: string | null;
    age: MetaCut[];
    gender: MetaCut[];
    region: MetaCut[];
    placement: MetaCut[];
    device: MetaCut[];
  };
  leads: {
    total: LeadCut;
    hour: LeadCut[];
    weekday: LeadCut[];
    placement: LeadCut[];
    phoneEntry: LeadCut[];
    risk: LeadCut[];
    browser: LeadCut[];
    reasons: {
      refused: number;
      noReason: number;
      top: Array<{
        reason: string;
        leads: number;
        rejected: number;
        trash: number;
        double: number;
        share: number;
      }>;
    };
  };
  minDecided: number;
  minImpressions: number;
}

/** Фильтры страницы: период — ключ @autoaff/schemas/period; пустая строка — фильтр не задан. */
export interface BreakdownsFilters {
  period: string;
  account: string;
  offer: string;
  state: string;
  q: string;
}

function useBreakdowns(f: BreakdownsFilters) {
  const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v !== '' && v !== 'all')).toString();
  return useQuery({
    queryKey: ['ads-breakdowns', f],
    queryFn: () => api<BreakdownsData>(`/api/ads/breakdowns?${qs}`),
    placeholderData: keepPreviousData,
    refetchInterval: 5 * 60_000,
  });
}

// ─── подписи ───────────────────────────────────────────────────────────
const GENDER: Record<string, string> = { female: 'Женщины', male: 'Мужчины', unknown: 'Не указан' };
const DEVICE: Record<string, string> = {
  iphone: 'iPhone',
  ipad: 'iPad',
  ipod: 'iPod',
  android_smartphone: 'Android, телефон',
  android_tablet: 'Android, планшет',
  desktop: 'Компьютер',
  other: 'Другое',
  unknown: 'Не указано',
};
const PLATFORM: Record<string, string> = {
  facebook: 'Facebook',
  fb: 'Facebook',
  instagram: 'Instagram',
  ig: 'Instagram',
  messenger: 'Messenger',
  msg: 'Messenger',
  audience_network: 'Audience Network',
  an: 'Audience Network',
  threads: 'Threads',
  th: 'Threads',
};
const POSITION: Record<string, string> = {
  feed: 'лента',
  reels: 'Reels',
  facebook_reels: 'Reels',
  instagram_reels: 'Reels',
  facebook_reels_overlay: 'Reels, наложение',
  story: 'истории',
  stories: 'истории',
  instagram_stories: 'истории',
  facebook_stories: 'истории',
  explore: 'Интересное',
  instagram_explore: 'Интересное',
  instagram_explore_grid_home: 'Интересное',
  explore_home: 'Интересное',
  marketplace: 'Marketplace',
  video_feeds: 'видео',
  right_hand_column: 'правая колонка',
  right_column: 'правая колонка',
  search: 'поиск',
  instagram_search: 'поиск',
  instream_video: 'видео в роликах',
  profile_feed: 'профиль',
  instagram_profile_feed: 'профиль',
  instagram_profile_reels: 'Reels в профиле',
  an_classic: 'сайты и приложения',
  classic: 'сайты и приложения',
  rewarded_video: 'видео с наградой',
  messenger_inbox: 'входящие',
  inbox: 'входящие',
  messenger_stories: 'истории',
  threads_feed: 'лента',
  unknown: 'не указано',
};
const REGION: Array<[RegExp, string]> = [
  [/^(tashkent|toshkent)( city| shahri)?$/i, 'Ташкент'],
  [/^(tashkent|toshkent)/i, 'Ташкентская обл.'],
  [/^(samarqand|samarkand)/i, 'Самаркандская обл.'],
  [/^(bukhara|buxoro)/i, 'Бухарская обл.'],
  [/^(andijan|andijon)/i, 'Андижанская обл.'],
  [/^(fergana|farg.ona)/i, 'Ферганская обл.'],
  [/^namangan/i, 'Наманганская обл.'],
  [/^(kashkadarya|qashqadaryo)/i, 'Кашкадарьинская обл.'],
  [/^(surkhandarya|surxondaryo)/i, 'Сурхандарьинская обл.'],
  [/^(jizzakh|jizzax|djizak)/i, 'Джизакская обл.'],
  [/^(syrdarya|sirdaryo)/i, 'Сырдарьинская обл.'],
  [/^(navoi|navoiy)/i, 'Навоийская обл.'],
  [/^(khorezm|xorazm)/i, 'Хорезмская обл.'],
  [/^(karakalpakstan|qoraqalpog)/i, 'Каракалпакстан'],
];
const PHONE_ENTRY: Record<string, string> = {
  typed: 'Набран вручную',
  autofill: 'Подставил браузер',
  'autofill-edit': 'Подставил и поправили',
  paste: 'Вставлен',
  unknown: 'Не определён',
  none: 'Не с нашего лендинга',
};
const RISK: Record<string, string> = {
  high: 'Высокий риск',
  medium: 'Средний риск',
  low: 'Низкий риск',
  none: 'Не проверялся',
};
const BROWSER: Record<string, string> = {
  instagram: 'Instagram, встроенный',
  facebook: 'Facebook, встроенный',
  regular: 'Обычный браузер',
  unknown: 'Не известно',
};
const WEEKDAY = ['Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота', 'Воскресенье'];

const human = (s: string) => s.replaceAll('_', ' ');

/** Место показа Meta «instagram|instagram_reels» → «Instagram · Reels». */
function metaPlacement(key: string): string {
  const [platform = '', position = ''] = key.split('|');
  const p = PLATFORM[platform] ?? human(platform);
  return position && position !== 'unknown' ? `${p} · ${POSITION[position] ?? human(position)}` : p;
}

/** Место показа из ссылки объявления: «instagram_reels», «facebook_mobile_feed», «ig» → «Instagram · Reels». */
function leadPlacement(key: string): string {
  if (key === 'unknown') return 'Не известно';
  if (key === 'other' || key === 'others') return 'Другие места';
  const m = /^(facebook|instagram|messenger|audience_network|threads|fb|ig|msg|an|th)(?:_(.+))?$/.exec(key);
  if (!m) return human(key);
  const platform = PLATFORM[m[1] ?? ''] ?? human(m[1] ?? '');
  const rest = (m[2] ?? '').replace(/^mobile_/, '');
  if (!rest) return platform;
  const desktop = rest.startsWith('desktop_');
  const pos = desktop ? rest.slice(8) : rest;
  return `${platform} · ${POSITION[pos] ?? human(pos)}${desktop ? ', компьютер' : ''}`;
}

const region = (key: string) =>
  key === 'unknown' ? 'Не указан' : (REGION.find(([re]) => re.test(key))?.[1] ?? key);
const age = (key: string) => (key.toLowerCase() === 'unknown' ? 'Не указан' : key.replace('-', '–'));

// ─── виды ──────────────────────────────────────────────────────────────
type MetaKind = 'age' | 'gender' | 'region' | 'placement' | 'device';
const META_KINDS: Array<{ value: MetaKind; label: string }> = [
  { value: 'age', label: 'Возраст' },
  { value: 'gender', label: 'Пол' },
  { value: 'region', label: 'Регион' },
  { value: 'placement', label: 'Место показа' },
  { value: 'device', label: 'Устройство' },
];
const META_LABEL: Record<MetaKind, (k: string) => string> = {
  age,
  gender: (k) => GENDER[k] ?? k,
  region,
  placement: metaPlacement,
  device: (k) => DEVICE[k] ?? human(k),
};

type LeadKind = 'hour' | 'weekday' | 'placement' | 'phoneEntry' | 'risk' | 'browser' | 'reasons';
const LEAD_KINDS: Array<{ value: LeadKind; label: string }> = [
  { value: 'hour', label: 'Час' },
  { value: 'weekday', label: 'День недели' },
  { value: 'placement', label: 'Место показа' },
  { value: 'phoneEntry', label: 'Ввод номера' },
  { value: 'risk', label: 'Риск' },
  { value: 'browser', label: 'Браузер' },
  { value: 'reasons', label: 'Причины отказа' },
];
const LEAD_HEAD: Record<Exclude<LeadKind, 'reasons'>, string> = {
  hour: 'Час (Ташкент)',
  weekday: 'День недели',
  placement: 'Место показа',
  phoneEntry: 'Как введён номер',
  risk: 'Риск антифрода',
  browser: 'Браузер',
};
const LEAD_LABEL: Record<Exclude<LeadKind, 'reasons'>, (k: string) => string> = {
  hour: (k) => `${k.padStart(2, '0')}:00–${String((Number(k) + 1) % 24).padStart(2, '0')}:00`,
  weekday: (k) => WEEKDAY[Number(k) - 1] ?? k,
  placement: leadPlacement,
  phoneEntry: (k) => PHONE_ENTRY[k] ?? k,
  risk: (k) => RISK[k] ?? k,
  browser: (k) => BROWSER[k] ?? k,
};

const orDash = (v: number | null, f: (n: number) => string) => (v === null ? '—' : f(v));
const dayWord = (day: string) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString('ru-RU', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'long',
  });

/** Переключатель внутри карточки: сегменты на компьютере, выпадающий список на телефоне. */
function Switch<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string }>;
}) {
  return (
    <>
      <div className="hide-m bd-seg">
        <Segmented<T> label={label} value={value} onChange={onChange} options={options} />
      </div>
      <div className="only-m">
        <FilterSelect<T>
          label={label}
          value={value}
          onChange={onChange}
          options={options}
          width={240}
          showValue
        />
      </div>
    </>
  );
}

/** Полоска доли: заливка акцентом; мало данных — приглушённо. */
function Bar({ value, muted, label }: { value: number | null; muted?: boolean; label: string }) {
  const v = value === null ? 0 : Math.max(0, Math.min(100, value));
  return (
    <span className={clsx('bd-bar', muted && 'is-muted')} role="img" aria-label={label}>
      <i style={{ width: `${v}%` }} />
    </span>
  );
}

// ─── страница ──────────────────────────────────────────────────────────
export function Breakdowns({
  filters,
  period,
  onView,
}: {
  filters: BreakdownsFilters;
  period: Period;
  onView: (v: AdsMode) => void;
}) {
  const r = useBreakdowns(filters);
  const d = r.data;
  return (
    <>
      <section
        className={clsx('card bd-card', r.isPlaceholderData && 'is-busy')}
        aria-busy={r.isFetching}
        aria-label="Разрезы Meta"
      >
        <header className="ads-table-head">
          <div className="hide-m">
            <Segmented<AdsMode> label="Разрез" value="breakdowns" onChange={onView} options={MODES} />
          </div>
          <div className="only-m">
            <FilterSelect<AdsMode>
              label="Разрез"
              value="breakdowns"
              onChange={onView}
              options={MODES}
              width={220}
              showValue
            />
          </div>
        </header>
        {r.isError && !d ? (
          <EmptyState
            icon={<ChartNoAxesColumn />}
            title="Не удалось загрузить разрезы"
            action={<Button onClick={() => r.refetch()}>Повторить</Button>}
          >
            Цифры не пришли с сервера. Проверьте интернет и повторите.
          </EmptyState>
        ) : (
          <MetaBlock d={d} period={period} />
        )}
      </section>
      {!(r.isError && !d) && <LeadsBlock d={d} period={period} busy={r.isPlaceholderData} />}
    </>
  );
}

function Loading({ rows = 5 }: { rows?: number }) {
  return (
    <div className="bd-loading">
      {[0, 1, 2, 3, 4, 5, 6].slice(0, rows).map((i) => (
        <Skeleton key={i} h={22} />
      ))}
    </div>
  );
}

// ─── разрезы Meta ──────────────────────────────────────────────────────
function MetaBlock({ d, period }: { d: BreakdownsData | undefined; period: Period }) {
  const [kind, setKind] = useRemembered<MetaKind>(
    'ads:bd-meta',
    'age',
    META_KINDS.map((k) => k.value),
  );
  const rows = d?.meta[kind] ?? [];
  const since = d?.period.since ?? null;
  const partial = d?.meta.from && (!since || since < d.meta.from);
  return (
    <div className="bd-block">
      <div className="bd-head">
        <div>
          <h2 className="card-title">Кто видит рекламу</h2>
          <p className="muted bd-sub">Разрезы Meta: расход, CTR, CPM и цена лида по пикселю</p>
        </div>
        <Switch label="Разрез Meta" value={kind} onChange={setKind} options={META_KINDS} />
      </div>
      {!d ? (
        <Loading />
      ) : rows.length === 0 ? (
        d.meta.from ? (
          <EmptyState icon={<Users />} title={`${capFirst(periodPhrase(period))} в разрезах Meta пусто`}>
            В эти дни не было расхода под выбранные фильтры. Разрезы Meta есть с {dayWord(d.meta.from)}.
          </EmptyState>
        ) : (
          <EmptyState icon={<Users />} title="Разрезы Meta ещё загружаются">
            Платформа забирает их раз в час: сначала сегодня и вчера, потом 30 дней истории.
          </EmptyState>
        )
      ) : (
        <>
          <div className="bd-wrap">
            <table className="tbl bd-tbl">
              <thead>
                <tr>
                  <th>{META_KINDS.find((k) => k.value === kind)?.label}</th>
                  <th className="num">Расход</th>
                  <th className="num">Показы</th>
                  <th className="num" title="Клики на ссылку от показов">
                    CTR
                  </th>
                  <th className="num" title="Цена 1000 показов">
                    CPM
                  </th>
                  <th className="num" title="Лиды по пикселю Meta — чтобы сравнить разрезы между собой">
                    Лиды пикселя
                  </th>
                  <th className="num" title="Расход / лиды по пикселю">
                    CPL
                  </th>
                  <th className="num" title="Досмотр 3 секунд от показов">
                    Досмотр 3 с
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((x) => (
                  <tr key={x.key} className={clsx(x.small && 'is-small')}>
                    <td className="bd-name">
                      <b>{META_LABEL[kind](x.key)}</b>
                      <span className="bd-share">
                        <Bar value={x.share} label={`Доля в расходе ${orDash(x.share, pct)}`} />
                        <small>
                          {orDash(x.share, pct)} расхода{x.small ? ' · мало показов' : ''}
                        </small>
                      </span>
                    </td>
                    <td className="num" data-label="Расход">
                      {usd(x.spend)}
                    </td>
                    <td className="num" data-label="Показы" data-m="hide">
                      {int(x.impressions)}
                    </td>
                    <td className="num" data-label="CTR">
                      {orDash(x.ctr, pct)}
                    </td>
                    <td className="num" data-label="CPM" data-m="hide">
                      {orDash(x.cpm, usd)}
                    </td>
                    <td className="num" data-label="Лиды пикселя" data-m="hide">
                      {x.leadsFb === null ? '—' : int(x.leadsFb)}
                    </td>
                    <td className="num" data-label="CPL">
                      {orDash(x.cpl, usd)}
                    </td>
                    <td className="num" data-label="Досмотр 3 с" data-m="hide">
                      {orDash(x.hookRate, pct)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="muted bd-note">
            {kind === 'region'
              ? 'По регионам Meta не отдаёт лиды пикселя — сравнивайте CTR и CPM, а апрув по местам показа — ниже.'
              : 'Лиды здесь — по пикселю Meta, только чтобы сравнить разрезы между собой; настоящие лиды и апрув — ниже и в итогах.'}
            {partial && d.meta.from ? ` Разрезы Meta есть с ${dayWord(d.meta.from)}.` : ''}
          </p>
        </>
      )}
    </div>
  );
}

// ─── апрув по разрезам лидов ───────────────────────────────────────────
function LeadsBlock({ d, period, busy }: { d: BreakdownsData | undefined; period: Period; busy: boolean }) {
  const [kind, setKind] = useRemembered<LeadKind>(
    'ads:bd-leads',
    'hour',
    LEAD_KINDS.map((k) => k.value),
  );
  const total = d?.leads.total;
  return (
    <section className={clsx('card bd-card', busy && 'is-busy')} aria-label="Апрув по разрезам лидов">
      <div className="bd-block">
        <div className="bd-head">
          <div>
            <h2 className="card-title">Апрув по разрезам лидов</h2>
            <p className="muted bd-sub">
              {total && total.leads > 0
                ? `${int(total.leads)} лидов, с решением сети ${int(total.decided)}${
                    total.approveRate !== null ? ` · апрув ${pct(total.approveRate)}` : ''
                  }`
                : 'Как сеть решает наших лидов: по времени, месту показа, вводу номера, риску и браузеру'}
            </p>
          </div>
          <Switch label="Разрез лидов" value={kind} onChange={setKind} options={LEAD_KINDS} />
        </div>
        {!d ? (
          <Loading />
        ) : d.leads.total.leads === 0 ? (
          <EmptyState icon={<Users />} title={`${capFirst(periodPhrase(period))} лидов нет`}>
            Под выбранные фильтры не пришло ни одного лида.
          </EmptyState>
        ) : kind === 'reasons' ? (
          <Reasons d={d} />
        ) : kind === 'hour' ? (
          <Hours rows={d.leads.hour} min={d.minDecided} />
        ) : (
          <LeadTable kind={kind} rows={d.leads[kind]} />
        )}
        {d && d.leads.total.leads > 0 && kind !== 'reasons' && (
          <p className="muted bd-note">
            Апрув — от лидов с решением сети (апрув, отказ, мусор, дубль), мусор и дубли — тоже от них. Меньше{' '}
            {d.minDecided} решённых — мало данных: процент пока случаен.
          </p>
        )}
      </div>
    </section>
  );
}

function ApproveCell({ x }: { x: LeadCut }) {
  return (
    <div className="bd-approve">
      <Bar
        value={x.approveRate}
        muted={x.small}
        label={`Апрув ${orDash(x.approveRate, pct)}${x.small ? ', мало данных' : ''}`}
      />
      <span className="tnum">{orDash(x.approveRate, pct)}</span>
    </div>
  );
}

function LeadTable({ kind, rows }: { kind: Exclude<LeadKind, 'reasons' | 'hour'>; rows: LeadCut[] }) {
  return (
    <div className="bd-wrap">
      <table className="tbl bd-tbl bd-leads">
        <thead>
          <tr>
            <th>{LEAD_HEAD[kind]}</th>
            <th className="num">Лиды</th>
            <th className="num">С решением</th>
            <th className="bd-approve-th">Апрув</th>
            <th className="num">Мусор и дубли</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((x) => (
            <tr key={x.key} className={clsx(x.small && 'is-small')}>
              <td className="bd-name">
                <b>{LEAD_LABEL[kind](x.key)}</b>
                {x.small && <span className="sub">мало данных</span>}
              </td>
              <td className="num" data-label="Лиды">
                {int(x.leads)}
              </td>
              <td className="num" data-label="С решением" data-m="hide">
                {int(x.decided)}
              </td>
              <td data-label="Апрув">
                <ApproveCell x={x} />
              </td>
              <td className="num" data-label="Мусор и дубли">
                {orDash(x.junkRate, pct)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Часы: столбики апрува по часам создания лида (Ташкент) и короткий вывод — лучший и худший час. */
function Hours({ rows, min }: { rows: LeadCut[]; min: number }) {
  const known = rows.filter((x) => !x.small && x.approveRate !== null);
  const best = [...known].sort((a, b) => (b.approveRate ?? 0) - (a.approveRate ?? 0))[0];
  const worst = [...known].sort((a, b) => (a.approveRate ?? 0) - (b.approveRate ?? 0))[0];
  const label = LEAD_LABEL.hour;
  return (
    <>
      <p className="bd-story">
        {best && worst && best.key !== worst.key ? (
          <>
            Лучший час — <b>{label(best.key)}</b>: апрув {pct(best.approveRate ?? 0)}. Хуже всего —{' '}
            <b>{label(worst.key)}</b>: {pct(worst.approveRate ?? 0)}.
          </>
        ) : (
          <>Сравнить часы пока нельзя: ни в одном часе нет {min} лидов с решением.</>
        )}
      </p>
      <div className="bd-chart">
        <div className="bd-axis" aria-hidden>
          <span>100%</span>
          <span>50%</span>
          <span>0</span>
        </div>
        <ul className="bd-hours" aria-label="Апрув по часам">
          {rows.map((x) => {
            const text = `${label(x.key)}: лидов ${x.leads}, с решением ${x.decided}${
              x.approveRate !== null ? `, апрув ${pct(x.approveRate)}` : ''
            }${x.small ? ', мало данных' : ''}`;
            return (
              <li
                key={x.key}
                className={clsx('bd-hour', x.small && 'is-muted')}
                aria-label={text}
                title={text}
              >
                <span className="bd-col">
                  <i style={{ height: `${x.approveRate ?? 0}%` }} />
                </span>
                <small aria-hidden>{Number(x.key) % 3 === 0 ? x.key.padStart(2, '0') : ''}</small>
              </li>
            );
          })}
        </ul>
      </div>
      <p className="muted bd-legend">
        Высота столбика — апрув в этот час; бледный — меньше {min} лидов с решением. Наведите на столбик —
        точные цифры.
      </p>
    </>
  );
}

function Reasons({ d }: { d: BreakdownsData }) {
  const r = d.leads.reasons;
  if (r.refused === 0)
    return (
      <EmptyState icon={<Users />} title="Отказов нет">
        За период сеть не отклонила ни одного лида.
      </EmptyState>
    );
  return (
    <>
      <p className="bd-story">
        Отказ, мусор или дубль — {int(r.refused)}{' '}
        {r.noReason > 0 ? `, у ${int(r.noReason)} сеть причину не указала.` : '.'}
      </p>
      {r.top.length > 0 && (
        <div className="bd-wrap">
          <table className="tbl bd-tbl bd-leads">
            <thead>
              <tr>
                <th>Причина от сети</th>
                <th className="num">Лиды</th>
                <th className="bd-approve-th">Доля отказов</th>
                <th className="num">Отказ · мусор · дубль</th>
              </tr>
            </thead>
            <tbody>
              {r.top.map((x) => (
                <tr key={x.reason}>
                  <td className="bd-name">
                    <b title={x.reason}>{x.reason}</b>
                  </td>
                  <td className="num" data-label="Лиды">
                    {int(x.leads)}
                  </td>
                  <td data-label="Доля отказов">
                    <div className="bd-approve">
                      <Bar value={x.share} label={`Доля ${pct(x.share)}`} />
                      <span className="tnum">{pct(x.share)}</span>
                    </div>
                  </td>
                  <td className="num" data-label="Отказ · мусор · дубль">
                    {x.rejected} · {x.trash} · {x.double}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

const capFirst = (s: string): string => `${s.charAt(0).toUpperCase()}${s.slice(1)}`;
